/**
 * STANDARD APPLIANCE SIZES (2026-10-15, Matt: "add typical standalone appliances sizes for
 * the user to choose from across the board"; Armando: "How do you edit the width of an
 * appliance? … change the dishwasher from 24" to 18", but the edit type option is grayed out").
 *
 * Every standalone appliance is sized per PLACEMENT now (the furniture rule — Width / Depth /
 * Height are instance values, 0 = the type's), and each kind offers its industry-typical
 * sizes as one pick (`applianceSize`): choosing one writes its width, depth and height onto
 * that appliance; typing a dimension afterwards makes it Custom. Nobody needs type-edit
 * rights to put an 18" dishwasher in a small kitchen.
 *
 * Sizes are nominal US residential (inches), from manufacturer install guides as typical
 * values — the opening a designer plans for, not one brand's cut sheet. A cooktop's height
 * is its body BELOW the counter; a TV's width × height is its panel with bezel.
 */

export interface ApplianceSize {
  value: string
  label: string
  /** inches */
  w: number
  d: number
  h: number
}

const S = (value: string, label: string, w: number, d: number, h: number): ApplianceSize => ({ value, label, w, d, h })

export const APPLIANCE_SIZES: Record<string, ApplianceSize[]> = {
  range: [
    S('24', '24" compact', 24, 26, 36),
    S('30', '30" standard', 30, 26, 36),
    S('30pro', '30" pro', 30, 29, 36),
    S('36pro', '36" pro', 36, 29, 36),
    S('48pro', '48" pro', 48, 29, 36),
    S('60pro', '60" pro', 60, 29, 36),
  ],
  cooktop: [
    S('24', '24"', 24, 20, 4),
    S('30', '30"', 30, 21, 4),
    S('36', '36"', 36, 21, 4),
    S('48', '48" rangetop', 48, 27, 9),
  ],
  fridge: [
    S('24', '24" apartment', 24, 28, 64),
    S('28', '28" top freezer', 28, 31, 66),
    S('30', '30" top freezer', 30, 32, 67),
    S('33', '33" bottom freezer', 33, 34, 69),
    S('36', '36" French door', 36, 35, 70),
    S('36cd', '36" counter-depth', 36, 28, 70),
  ],
  columnFridge: [
    S('18', '18" column', 18, 24, 84),
    S('24', '24" column', 24, 24, 84),
    S('30', '30" column', 30, 24, 84),
    S('36', '36" column', 36, 24, 84),
  ],
  // the INSERT's width — 2" under the hood enclosure it sits in
  hoodInsert: [
    S('28', '28" (30" hood)', 28, 19, 10),
    S('34', '34" (36" hood)', 34, 19, 10),
    S('40', '40" (42" hood)', 40, 19, 10),
    S('46', '46" (48" hood)', 46, 19, 10),
    S('52', '52" (54" hood)', 52, 19, 10),
    S('58', '58" (60" hood)', 58, 22, 10),
  ],

  dishwasher: [
    S('18', '18" (ADA / compact)', 18, 24, 34),
    S('24', '24" standard', 24, 24, 34),
  ],
  microwaveDrawer: [
    S('24', '24"', 24, 23, 15),
    S('30', '30"', 30, 23, 15),
  ],
  wallOven: [
    S('24', '24" single', 24, 23, 24),
    S('27', '27" single', 27, 24, 28.5),
    S('30', '30" single', 30, 24, 28.5),
    S('27d', '27" double', 27, 24, 50),
    S('30d', '30" double', 30, 24, 51),
    S('30c', '30" oven + microwave', 30, 24, 42),
  ],
  beverageCenter: [
    S('15', '15"', 15, 24, 34),
    S('18', '18"', 18, 24, 34),
    S('24', '24"', 24, 24, 34),
  ],
  iceMaker: [
    S('15', '15"', 15, 24, 34),
    S('18', '18"', 18, 24, 34),
  ],
  washer: [
    S('24', '24" compact', 24, 25, 34),
    S('27f', '27" front-load', 27, 31, 39),
    S('27t', '27" top-load', 27, 28, 42),
  ],
  dryer: [
    S('24', '24" compact', 24, 25, 34),
    S('27', '27" standard', 27, 31, 39),
  ],
  washerDryerCombo: [
    S('24', '24" stacked', 24, 25, 67),
    S('27', '27" stacked', 27, 31, 77),
  ],
  fireplaceLinear: [
    S('36', '36" viewing', 38, 21, 36),
    S('48', '48" viewing', 50, 21, 36),
    S('60', '60" viewing', 62, 21, 36),
    S('72', '72" viewing', 74, 21, 36),
  ],
  fireplaceWood: [
    S('36', '36" opening', 40, 26, 48),
    S('42', '42" opening', 46, 26, 48),
    S('48', '48" opening', 52, 26, 48),
  ],
  woodStove: [
    S('s', 'Small', 20, 22, 28),
    S('m', 'Medium', 24, 26, 32),
    S('l', 'Large', 28, 28, 34),
  ],
  wallTV: [
    S('43', '43" screen', 38.5, 3.5, 22.5),
    S('55', '55" screen', 49, 3.5, 28.5),
    S('65', '65" screen', 57.5, 3.5, 33.5),
    S('75', '75" screen', 66.5, 3.5, 38.5),
    S('85', '85" screen', 75, 3.5, 43.5),
  ],
  waterHeater: [
    S('40', '40 gal', 20, 20, 58),
    S('50', '50 gal', 22, 22, 60),
    S('75', '75 gal', 26, 26, 64),
  ],
}

/** kinds that offer standard sizes (and so are sized per placement) */
export const APPLIANCE_SIZED_KINDS: readonly string[] = Object.keys(APPLIANCE_SIZES)

export function applianceSizeOf(kind: unknown, value: unknown): ApplianceSize | null {
  return APPLIANCE_SIZES[String(kind || '')]?.find((s) => s.value === value) ?? null
}

/** the size pick's choices for one kind ('' = custom / the type's own size) */
export function applianceSizeChoices(kind: string): { value: string; label: string }[] {
  return [{ value: '', label: 'Custom' }, ...(APPLIANCE_SIZES[kind] ?? []).map((s) => ({ value: s.value, label: s.label }))]
}
